import { Camera, CameraSource } from "@capacitor/camera";
import ons from "onsenui";
import React from "react";
import { getDevicePhotoForDocument } from "../get_device_photo";
import { usePermissionDeniedModal } from "../hooks/usePermissionDeniedModal";
import { PatientDocumentScanCreate } from "../messages/PatientDocuments";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import DocumentUploadBottomSheet from "./DocumentUploadBottomSheet";
import { openScanner } from "./MedflytScanner";
import { simpleAlert } from "./SimpleAlert";

interface Props {
  isDocumentNameNeeded: boolean;
  isScanningNewDocument: boolean;
  handleCloseDocumentNameDialog?: () => void;
  onScanSuccessCallback: (data: PatientDocumentScanCreate) => void;
  onScanErrorCallback: (message: string) => void;
}

const DocumentUploadActionsActivity = ({
  handleCloseDocumentNameDialog,
  isScanningNewDocument,
  onScanSuccessCallback,
  onScanErrorCallback,
  isDocumentNameNeeded,
}: Props) => {
  const [documentName, setDocumentName] = React.useState(null);
  const [isUploadOptionsOpen, setUploadOptionsOpen] = React.useState(false);
  const { openModal: openPermissionDeniedModal } = usePermissionDeniedModal();

  const handleUploadFromCamera = async () => {
    const { camera: cameraPermission } = await Camera.checkPermissions();

    if (cameraPermission === "denied") {
      openPermissionDeniedModal("camera");
      return;
    }

    openScanner(
      (base64) => {
        onScanSuccessCallback({
          documentTitle: documentName,
          base64: base64,
          documentId: null,
          documentVersion: null,
        });
      },
      onScanErrorCallback,
      {}
    );
  };

  const handleClickOpenFromGallery = async () => {
    const { photos: photosPermission } = await Camera.checkPermissions();

    if (photosPermission === "denied") {
      openPermissionDeniedModal("photos");
      return;
    }

    const photo = await getDevicePhotoForDocument({
      width: 1080,
      height: 1080,
      source: CameraSource.Photos,
    });

    if (photo !== null) {
      return onScanSuccessCallback({
        documentTitle: documentName,
        base64: photo,
        documentId: null,
        documentVersion: null,
      });
    }
  };

  const handleScanNewDocument = React.useCallback(async () => {
    if (isDocumentNameNeeded) {
      const docName: any = await ons.notification.prompt(" ", {
        animation: "none",
        modifier: "material",
        cancelable: true,
        title: translate(STRINGS.ENTER_DOCUMENT_NAME),
      });

      if (docName !== null) {
        setUploadOptionsOpen(true);
        setDocumentName(docName);
      } else {
        simpleAlert({
          icon: "error",
          message: translate(STRINGS.INVALID_DOCUMENT_NAME),
          title: translate(STRINGS.ERROR),
        });
      }
      if (handleCloseDocumentNameDialog !== undefined) {
        handleCloseDocumentNameDialog();
      }
    } else {
      setUploadOptionsOpen(true);
    }
  }, [setUploadOptionsOpen, setDocumentName, handleCloseDocumentNameDialog, isDocumentNameNeeded]);

  React.useEffect(() => {
    if (isScanningNewDocument) {
      handleScanNewDocument();
    }
  }, [isScanningNewDocument, handleScanNewDocument]);

  return (
    <DocumentUploadBottomSheet
      isScanningNewDocument={isScanningNewDocument}
      handleCloseDocumentNameDialog={handleCloseDocumentNameDialog}
      isUploadOptionsOpen={isUploadOptionsOpen}
      setUploadOptionsOpen={setUploadOptionsOpen}
      handleClickOpenFromGallery={handleClickOpenFromGallery}
      handleScanNewDocument={handleScanNewDocument}
      handleUploadFromCamera={handleUploadFromCamera}
    />
  );
};

export default React.memo(DocumentUploadActionsActivity);
